import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Lock, Loader2, BookOpen, Sparkles, ChevronRight, X, AlertTriangle, Info, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ClientStatusPipeline from '../components/ClientStatusPipeline';
import { ScreeningPanel } from '@/components/ScreeningPanel';
import {
  CitedText,
  SectionRefStrip,
  ReferenceRail,
  CompanyProfileBlock,
  CompetitionBlock,
  GenericSection,
} from '@/lib/deepDiveRender';
import { normalizeStageLabel } from '../utils/stage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier palette mirrors the internal CRM so the committee sees the same
// language used by the analyst team.
const TIER_BADGE = {
  'Strategic Priority': 'bg-violet-100 text-violet-800 border-violet-300',
  'High Priority':      'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Medium':             'bg-sky-100 text-sky-800 border-sky-300',
  'Monitor':            'bg-amber-100 text-amber-800 border-amber-300',
  'Low Priority':       'bg-orange-100 text-orange-800 border-orange-300',
  'Not Suitable':       'bg-rose-100 text-rose-900 border-rose-400',
  'Could Not Assess':   'bg-slate-100 text-slate-700 border-slate-300',
  'Awaiting AI':        'bg-indigo-50 text-indigo-700 border-indigo-200',
};

// Viatris Fit Screen constants (Feb 2026). Mirror of the constants in
// ViatrisClientDashboard so the Reference Library filters identically.
// Two-gate client scope: Phase 3+ AND TA-of-interest. Off-TA assets
// are dropped from the visible tables so the tier counts match the
// Admin Recommendation Distribution exactly (which applies both
// filters when the "Viatris scope" button is ON). Full-universe
// download link remains scope-agnostic.
const VIATRIS_PRIORITY_TA_LABEL = 'Cardiovascular · Metabolism · CNS · Immunology · Dermatology · Pain'; // eslint-disable-line no-unused-vars

// Sep 2026 — Reference Library is the COMPLETE asset index for the
// cohort, not just parked/out-of-scope assets. Render in tier order
// (best first) so the committee can scan top → bottom and see exactly
// where every asset they evaluated landed in the funnel.
// (Feb 2026 · v2) Reverted CNA rollup — the client Reference Library
// is now a strict 1:1 transposition of the Admin Recommendation
// Distribution (user request). Could Not Assess surfaces as its own
// chip so the tier totals match exactly across surfaces.
const TIER_ORDER = [
  'Strategic Priority',
  'High Priority',
  'Medium',
  'Monitor',
  'Low Priority',
  'Not Suitable',
  'Could Not Assess',
  'Awaiting AI',
];

// (Feb 2026) Short one-line explanation shown on every tier chip so
// the committee can see the score threshold that gets an asset into
// that tier without opening the Scoring Rubric modal.
const TIER_THRESHOLD = {
  'Strategic Priority': 'Weighted ≥ 4.5 / 5',
  'High Priority':      'Weighted 3.75 – 4.5',
  'Medium':             'Weighted 3.0 – 3.75',
  'Monitor':            'Weighted 2.0 – 3.0',
  'Low Priority':       'Weighted < 2.0',
  'Not Suitable':       'AI qualitative call · deal-killer',
  'Could Not Assess':   'AI pipeline could not evaluate',
  'Awaiting AI':        'Eligible · AI assessment queued',
};

export default function ViatrisReferenceLibraryPage() {
  const { token } = useParams();
  const [data, setData] = useState({ assets: [], cohort_name: '', total: 0, by_tier: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDeepDive, setActiveDeepDive] = useState(null);
  // (Feb 2026 EMERGENCY — demo-day) Full Universe filter removed
  // entirely per client request. Reference Library once again only
  // shows the current cohort's assets. Explicit Download Excel action
  // still available in the header. Restore Full Universe post-demo
  // once the loop root-cause is fully understood.
  // Default tier filter is null = show ALL buckets in the current
  // cohort so the committee sees the complete asset index on open.
  const [tierFilter, setTierFilter] = useState(null);
  // View mode — 'grouped' shows assets bucketed by tier (default,
  // most intuitive for a comprehensive index); 'table' is one flat
  // sortable view for power users.
  const [viewMode, setViewMode] = useState('grouped');
  // Status Pipeline (Feb 2026 · parity with Admin) — 5-stage narrowing
  // funnel. Clicking a stage filters the tier groups + table below.
  const [pipelineData, setPipelineData] = useState({ counts: {}, cohort_name: '' });
  const [pipelineStage, setPipelineStage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API}/client/viatris/${token}/reference-library`),
        axios.get(`${API}/client/viatris/${token}/pipeline`),
      ]);
      setData(r.data || { assets: [], cohort_name: '', total: 0, by_tier: {} });
      setPipelineData(p.data || { counts: {}, cohort_name: '' });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invalid share link or library unavailable');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Lock className="w-12 h-12 mx-auto text-slate-400 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Access denied</h2>
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Jun 2026 — no client-side re-filtering. The backend returns the
  // reconciled eligible universe (same set as Admin "Eligible for AI").
  const all = data?.assets || [];

  // Pipeline-stage filter (Feb 2026 · parity with Admin StatusPipeline).
  // Applied BEFORE the tier chip filter so stage + tier compose.
  const ACTIVE_TIERS = new Set(['Strategic Priority', 'High Priority', 'Medium']);
  const pipelineFiltered = all.filter((r) => {
    if (!pipelineStage) return true;
    if (pipelineStage === 'cohort' || pipelineStage === 'phase_3_plus'
        || pipelineStage === 'universe' || pipelineStage === 'eligible') return true;
    if (pipelineStage === 'ai_scored') return r.recommendation !== 'Awaiting AI';
    if (pipelineStage === 'ta_of_interest') return !r.off_strategy_reason;
    if (pipelineStage === 'committee_review') return ACTIVE_TIERS.has(r.recommendation);
    if (pipelineStage === 'finalised_assets' || pipelineStage === 'finalised') {
      const scored = r.members_submitted_count ?? r.members_scored_count ?? 0;
      const total = r.total_members ?? 6;
      return scored >= total;
    }
    return true;
  });

  const visible = tierFilter ? pipelineFiltered.filter((r) => r.recommendation === tierFilter) : pipelineFiltered;

  // Tier breakdown for the strip at top (derived client-side so the
  // counts always reflect the in-scope + pipeline filter).
  const tierCounts = pipelineFiltered.reduce((acc, r) => {
    const t = r.recommendation || 'Awaiting AI';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  // Group visible assets by tier (used by the 'grouped' view).
  const groupedByTier = TIER_ORDER.map((t) => ({
    tier: t,
    assets: visible.filter((a) => (a.recommendation || 'Awaiting AI') === t),
  })).filter((g) => g.assets.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" data-testid="reference-library-page">
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indigo-300 mb-0.5 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Powered by the ForesightRx Intelligence Platform (FIP)
            </div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> Reference Library
            </h1>
            <div className="text-[10px] text-slate-400">
              {data.cohort_name && <>Scope · <strong>{data.cohort_name}</strong> · </>}
              Complete index · {all.length} screened asset{all.length === 1 ? '' : 's'} across all tiers
            </div>
          </div>
          {/* View toggle — grouped (default) vs flat table */}
          <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-md p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('grouped')}
              className={`text-[11px] px-2.5 py-1 rounded transition-colors ${
                viewMode === 'grouped' ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="view-mode-grouped"
            >
              Grouped by tier
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`text-[11px] px-2.5 py-1 rounded transition-colors ${
                viewMode === 'table' ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="view-mode-table"
            >
              Flat table
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Screening Rules — the exact Inclusion / Exclusion criteria the
            ForesightRx team applies to derive this library. Read-only —
            same panel the committee sees on the main dashboard. */}
        <ScreeningPanel engagementId="eng-viatris-china" compact editable={false} clientToken={token} />

        {/* Screening Funnel — full Admin-parity explainable funnel.
            Same stages, colours and info tooltips as the internal
            team's view. Clicking an AI-side stage filters the tier
            groups + table below. */}
        <ClientStatusPipeline
          counts={pipelineData?.counts || {}}
          stages={pipelineData?.stages || []}
          cohortName={pipelineData?.cohort_name || data?.cohort_name}
          activeStage={pipelineStage}
          onStageClick={(k) => setPipelineStage(k)}
        />

        {/* Tier filter strip — Feb 2026 (EMERGENCY demo-day layout).
            Full Universe and Current Cohort tiles removed per client
            request; keeping the strip pared to All + 6 tier chips so
            no filter click can trigger a state cascade. Restore the
            richer layout post-demo. */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-2" data-testid="tier-strip">
          <button
            onClick={() => setTierFilter(null)}
            className={`border rounded-lg px-3 py-2 text-left transition-all bg-white border-slate-300 ${
              tierFilter === null ? 'ring-2 ring-offset-1 ring-slate-700 shadow-sm' : 'hover:shadow-sm'
            }`}
            data-testid="tier-all"
            title="Show every asset in this cohort across all tiers"
          >
            <div className="flex items-baseline justify-between gap-1">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-700">All</div>
              <div className="text-sm font-bold text-slate-800">{all.length}</div>
            </div>
            <div className="text-[9px] mt-0.5 opacity-70 truncate text-slate-600">Every tier · unfiltered</div>
          </button>
          {TIER_ORDER.map((t) => {
            const n = tierCounts[t] || 0;
            const active = tierFilter === t;
            const empty = n === 0;
            return (
              <button
                key={t}
                onClick={() => !empty && setTierFilter(active ? null : t)}
                disabled={empty}
                className={`border rounded-lg px-3 py-2 text-left transition-all ${
                  TIER_BADGE[t] || 'bg-white border-slate-200'
                } ${active ? 'ring-2 ring-offset-1 ring-violet-500 shadow-sm' : empty ? 'opacity-50 cursor-default' : 'hover:shadow-sm cursor-pointer'}`}
                data-testid={`tier-${t.replace(/\s+/g, '-').toLowerCase()}`}
                title={empty ? `${t} · no assets in this tier · ${TIER_THRESHOLD[t] || ''}` : `${t} · click to filter · ${TIER_THRESHOLD[t] || ''}`}
              >
                <div className="flex items-baseline justify-between gap-1">
                  <div className="text-[10px] uppercase tracking-wide font-semibold leading-tight">{t}</div>
                  <div className={`text-sm font-bold ${empty ? 'text-slate-400' : ''}`}>{n}</div>
                </div>
                {TIER_THRESHOLD[t] && (
                  <div className="text-[9px] mt-0.5 opacity-70 truncate" title={TIER_THRESHOLD[t]}>
                    {TIER_THRESHOLD[t]}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            {pipelineStage && (
              <div className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="ref-pipeline-stage-active">
                Pipeline stage: <strong>{
                  pipelineStage === 'ta_of_interest' ? 'TA of Interest'
                  : pipelineStage === 'committee_review' ? 'Committee Review'
                  : (pipelineStage === 'finalised_assets' || pipelineStage === 'finalised') ? 'Finalised'
                  : pipelineStage === 'phase_3_plus' ? 'Phase 3+'
                  : pipelineStage === 'eligible' ? 'Eligible for AI'
                  : pipelineStage === 'ai_scored' ? 'AI Assessed'
                  : 'Universe'
                }</strong>
                <button onClick={() => setPipelineStage(null)} className="text-emerald-700 underline ml-1" data-testid="ref-clear-pipeline-filter">
                  clear
                </button>
              </div>
            )}
            {tierFilter ? (
              <div className="text-xs text-violet-700 inline-flex items-center gap-1">
                Filtering: <strong>{tierFilter}</strong>
                <button onClick={() => setTierFilter(null)} className="text-violet-600 underline ml-1" data-testid="clear-tier-filter">
                  clear filter
                </button>
              </div>
            ) : null}
          </div>
          <a
            href={`${API}/client/viatris/${token}/download/master-list`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] text-indigo-700 hover:text-indigo-900 border border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 rounded px-2.5 py-1 transition-colors"
            data-testid="download-excel-btn"
            title="Download the Master List Excel (partneringONE layout — same workbook the ForesightRx team exports)"
          >
            <Download className="w-3 h-3" />
            Download Excel
          </a>
        </div>

        {viewMode === 'grouped' ? (
          groupedByTier.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-slate-500">No assets match this filter.</CardContent></Card>
          ) : groupedByTier.map((g) => (
            <Card key={g.tier} data-testid={`tier-group-${g.tier.replace(/\s+/g, '-').toLowerCase()}`}>
              <CardContent className="p-0">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/60 flex items-center gap-2">
                  <Badge className={`text-[10px] border ${TIER_BADGE[g.tier] || 'bg-slate-100'}`}>
                    {g.tier}
                  </Badge>
                  <span className="text-[11px] text-slate-500 font-medium">
                    {g.assets.length} asset{g.assets.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ReferenceTable rows={g.assets} onDeepDive={setActiveDeepDive} />
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-0">
              <ReferenceTable rows={visible} onDeepDive={setActiveDeepDive} />
            </CardContent>
          </Card>
        )}
      </main>

      {activeDeepDive && (
        <RefDeepDiveModal
          token={token}
          asset={activeDeepDive}
          onClose={() => setActiveDeepDive(null)}
        />
      )}
    </div>
  );
}


// Threshold for "scored high yet not in top tier" — weighted avg ≥ 2.78 / 5
// (equivalent to composite ≥ 25 / 45) marks an asset whose committee/AI
// rubric score suggests it deserves serious consideration even though the
// math landed it in Monitor / Low Priority / Not Suitable. The (i) icon
// exposes Claude's rationale for those rows so the committee
// understands *why* a decent-scoring asset isn't in the top buckets.
// (Feb 2026) Reviewer transparency — Claude assigns the display tier
// holistically (deal-killers, crowded space, IP, commercial fit),
// NOT purely from the 9-dimension weighted average. When Claude's
// tier disagrees with the math (up OR down), the assessment carries
// `ai_concern` with the direction and rationale. The (i) chip below
// exposes that rationale inline on every row that diverges so the
// operator can defend the tier assignment when the client points at
// "3.8/5 = Not Suitable" or "3.4/5 = High Priority" seeming
// mismatches. Also fires on UPGRADES (previously we only surfaced
// downgrades, which meant Dupixent-biosimilar-style upgrades looked
// unexplained).
function showWhyOff(row) {
  // (Feb 2026) Display the (i) chip whenever the AI's holistic tier
  // diverges from the score-derived math band — regardless of whether
  // we have a text rationale attached. An empty rationale still
  // signals a divergence worth surfacing; the popover will fall back
  // to "AI moved this from <math> → <claude>, no detailed rationale
  // captured" so the committee at least sees WHERE the AI landed
  // vs. where the math anchor sits.
  if (!row?.ai_concern) return false;
  const rn = row.ai_concern.rationale;
  const md = row.ai_concern.math_recommendation;
  const cd = row.ai_concern.claude_recommendation;
  // Fire on any recorded divergence OR any captured rationale.
  return Boolean(rn || (md && cd && md !== cd));
}


// RefAIVerdictChip — small inline chip that surfaces Claude's holistic
// tier override right next to the recommendation badge. Mirrors the
// AIConcernChip used on the client Cohort Scoring Matrix so the
// committee sees the SAME visible language on both surfaces when the
// AI's verdict overrides the raw math tier. Colour flips sky-blue for
// elevations (AI upgraded above math band) and amber for lowerings
// (AI downgraded below math band). Hover reveals the exact math →
// AI band delta plus the first line of Claude's rationale.
function RefAIVerdictChip({ asset }) {
  const ac = asset.ai_concern || {};
  const dir = ac.direction || 'downgrade';
  const isUp = dir === 'upgrade';
  const cls = isUp
    ? 'bg-sky-50 text-sky-800 border-sky-300'
    : 'bg-amber-50 text-amber-800 border-amber-300';
  const label = isUp ? 'AI elevated' : 'AI lowered';
  const claudeTier = ac.claude_recommendation || asset.recommendation;
  const tooltipParts = [
    isUp
      ? `AI elevated this asset above the math tier (${ac.math_recommendation || '—'}) up to ${claudeTier || '—'}.`
      : `AI lowered this asset below the math tier (${ac.math_recommendation || '—'}) down to ${claudeTier || '—'}.`,
    'The score-derived band is what the math says; the AI verdict is a holistic override that factors competition, IP, commercial fit and strategic alignment.',
  ];
  if (ac.rationale) tooltipParts.push(`\nWhy: ${ac.rationale}`);
  if (Array.isArray(ac.key_risks) && ac.key_risks.length > 0) {
    tooltipParts.push(`\nKey risks:\n• ${ac.key_risks.slice(0, 5).join('\n• ')}`);
  }
  return (
    <Badge
      className={`text-[9px] border inline-flex items-center gap-0.5 ${cls}`}
      title={tooltipParts.join('\n')}
      data-testid={`ref-ai-verdict-${asset.ea_id}`}
    >
      <AlertTriangle className="w-2.5 h-2.5" />
      {label} → {claudeTier || '—'}
    </Badge>
  );
}


// WhyOffTopTierInfo → replaced by universal WhyRecommendationInfo (Feb 2026 ·
// user request "let us have a rationale just in front of the recommendation").
// Same popover format now surfaces on Admin, Client Dashboard, AND the
// Reference Library so users see one consistent 'why' explanation.
import WhyRecommendationInfo from '../components/WhyRecommendationInfo';



// ReferenceTable — shared table renderer used by both the grouped view
// (one table per tier) and the flat table view (one big table).

// (Feb 2026 update) The full AIConcernChip + inline rationale strip
// that previously lived in the asset cell was replaced by a compact
// (i) icon next to the recommendation badge — see WhyOffTopTierInfo
// above. The chip is still used on the main client dashboard (Review
// Card and Cohort Scoring Matrix) but the Library surface keeps the
// table dense by surfacing the rationale only on demand.


function ReferenceTable({ rows, onDeepDive }) {
  if (!rows.length) {
    return <div className="py-8 text-center text-sm text-slate-400">No assets match this filter.</div>;
  }
  return (
    <table className="w-full text-xs table-fixed" data-testid="reference-table">
      <colgroup>
        <col style={{ width: '18%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '13%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '6%' }} />
      </colgroup>
      <thead className="text-slate-500 uppercase tracking-wide text-[10px] border-b border-slate-200 bg-slate-50">
        <tr>
          <th className="px-3 py-2 text-left">Asset</th>
          <th className="px-3 py-2 text-left">Indication</th>
          <th className="px-3 py-2 text-left">Stage</th>
          <th className="px-3 py-2 text-left">Modality</th>
          <th className="px-3 py-2 text-right">Score</th>
          <th className="px-3 py-2 text-left">Recommendation</th>
          <th className="px-3 py-2 text-left">Company</th>
          <th className="px-3 py-2 text-right"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.ea_id || r.asset_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`ref-row-${r.ea_id || r.asset_id}`}>
            <td className="px-3 py-2 font-semibold text-slate-800 align-top" title={r.asset_name}>
              <div className="truncate">{r.asset_name}</div>
              {/* Feb 2026 · user request — "you can remove all these
                  tags where AI elevated / AI lowered". RefAIVerdictChip
                  no longer rendered here. Rationale for the tier is
                  surfaced through the WhyRecommendationInfo (i) icon
                  next to the Recommendation badge in the next <td>. */}
              {/* Feb 2026 · user request — Off-stage / Off-strategy-TA
                  chip removed entirely from the UI. The tier column
                  already conveys "Not Suitable"; keeping the badge only
                  added redundant terminology. */}
            </td>
            <td className="px-3 py-2 text-slate-600 truncate" title={r.indication || ''}>{r.indication || '—'}</td>
            <td className="px-3 py-2 text-slate-600 truncate" title={r.phase || ''}>{normalizeStageLabel(r.phase) || '—'}</td>
            <td className="px-3 py-2 text-slate-600 truncate" title={r.modality || ''}>{r.modality || '—'}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-800 whitespace-nowrap">
              {r.composite_score != null
                ? (r.weighted_avg_score ?? r.composite_score / 9).toFixed(2)
                : '—'}
              <span className="text-[9px] text-slate-400"> /5</span>
            </td>
            <td className="px-3 py-2">
              <div className="inline-flex items-center gap-1">
                <Badge className={`text-[10px] border ${TIER_BADGE[r.recommendation] || 'bg-slate-100 text-slate-700'} truncate max-w-full`} title={r.recommendation}>
                  {r.recommendation || '—'}
                </Badge>
                <WhyRecommendationInfo asset={r} testIdPrefix="ref-lib-rec" />
              </div>
            </td>
            <td className="px-3 py-2 font-semibold text-slate-700 truncate" title={r.biotech_name}>{r.biotech_name}</td>
            <td className="px-3 py-2 text-right">
              {r.recommendation !== 'Awaiting AI' && r.ea_id ? (
                <button
                  type="button"
                  onClick={() => onDeepDive(r)}
                  className="text-[11px] text-indigo-700 hover:underline inline-flex items-center gap-0.5"
                  data-testid={`ref-deep-dive-${r.ea_id}`}
                >
                  Deep Dive <ChevronRight className="w-3 h-3" />
                </button>
              ) : (
                <span className="text-[10px] text-slate-400">Awaiting AI</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function RefDeepDiveModal({ token, asset, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('exec');
  // Highlight state for the persistent right-rail Reference Library
  // (Feb 2026 client request) — updated by clicks on inline [N] chips
  // in prose OR by the per-section "References cited here" strip.
  const [highlightN, setHighlightN] = useState(null);
  const onCite = (n) => setHighlightN(n);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/client/viatris/${token}/assets/${asset.ea_id}/deep-dive`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load deep dive');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, asset.ea_id]);

  // References tab dropped (Feb 2026) — replaced by the persistent
  // right-rail Reference Library rendered alongside every content tab.
  // Latest Alerts tab also removed (Feb 2026) — alerts remain on the
  // main dashboard "Latest Intelligence" surface.
  const TABS = [
    { key: 'exec',    label: 'Executive Summary' },
    { key: 'scoring', label: 'Scoring' },
    { key: 'company', label: 'Company' },
    { key: 'asset',   label: 'Asset' },
    { key: 'science', label: 'Science' },
    { key: 'comp',    label: 'Competition' },
  ];
  const refs = Array.isArray(data?.references) ? data.references : [];
  const refsBySection = data?.references_by_section || {};
  const maxRef = refs.length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ref-deep-dive-modal">
        <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-lg text-slate-800 truncate" title={asset.asset_name}>{asset.asset_name}</h3>
              {data?.composite_score != null && (
                <span
                  className="inline-flex items-baseline gap-0.5 text-xs px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800"
                  title="Weighted average per scored dimension (1–5 scale)"
                >
                  <span className="font-bold">{(data.weighted_avg_score ?? data.composite_score / 9).toFixed(2)}</span>
                  <span className="text-[10px] opacity-70">/5</span>
                </span>
              )}
              {data?.recommendation && (
                <Badge className={`text-[10px] border ${TIER_BADGE[data.recommendation] || 'bg-slate-100 text-slate-700'}`}>
                  {data.recommendation}
                </Badge>
              )}
              {/* Math-first derived verdict — same logic + thresholds
                  as the Cohort Scoring Matrix on the dashboard, so the
                  committee sees consistent verdict language across
                  every surface. (Feb 2026) Fires off the committee
                  weighted average /5 alone (FRx retired from client-visible portal).
                  In-License ≥ 4.0, Reassess 3.0–3.99, Decline < 3.0. */}
              {(() => {
                const weighted = data?.weighted_avg_score ?? (
                  data?.composite_score != null ? data.composite_score / 9 : null
                );
                if (weighted == null) return null;
                let word = 'Decline', cls = 'bg-rose-100 text-rose-900 border-rose-300';
                if (weighted >= 4.0) { word = 'In-License'; cls = 'bg-emerald-100 text-emerald-900 border-emerald-300'; }
                else if (weighted >= 3.0) { word = 'Reassess'; cls = 'bg-sky-100 text-sky-900 border-sky-300'; }
                return (
                  <Badge
                    className={`text-[11px] border font-bold ${cls}`}
                    title={`Committee weighted avg ${weighted.toFixed(2)}/5 · ≥ 4.0 In-License · ≥ 3.0 Reassess · < 3.0 Decline`}
                    data-testid="hero-verdict-badge"
                  >
                    Verdict: {word}
                  </Badge>
                );
              })()}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {asset.indication} · {normalizeStageLabel(asset.phase) || '—'} · {asset.modality} · {asset.biotech_name}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="ref-deep-dive-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-3 border-b border-slate-200 flex gap-1 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-xs px-3 py-1.5 rounded-t border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-indigo-600 text-indigo-700 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`ref-deep-dive-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          <div className="flex-1 overflow-y-auto p-5 text-sm min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : error ? (
              <div className="text-rose-700 text-sm">{error}</div>
            ) : !data?.has_assessment ? (
              <div className="text-slate-500 text-sm">{data?.message || 'No assessment available for this asset yet.'}</div>
            ) : (
              <>
                {tab === 'exec'    && <ExecBlock    exec={data.executive_summary} refIds={refsBySection.executive_summary} onCite={onCite} maxRef={maxRef} />}
                {tab === 'scoring' && <ScoringBlock scoring={data.scoring} composite={data.composite_score} weighted={data.weighted_avg_score} />}
                {tab === 'company' && <CompanyProfileBlock data={data.company_intelligence}  refIds={refsBySection.company_intelligence}  onCite={onCite} maxRef={maxRef} />}
                {tab === 'asset'   && <GenericSection title="Asset Intelligence"     data={data.asset_intelligence}    refIds={refsBySection.asset_intelligence}    onCite={onCite} maxRef={maxRef} />}
                {tab === 'science' && <GenericSection title="Scientific Assessment"  data={data.scientific_assessment} refIds={refsBySection.scientific_assessment} onCite={onCite} maxRef={maxRef} />}
                {tab === 'comp'    && <CompetitionBlock data={data.competitive_landscape} refIds={refsBySection.competitive_landscape} onCite={onCite} maxRef={maxRef} />}
              </>
            )}
          </div>
          {/* Persistent right-rail Reference Library — hidden on scoring
              tab (score dims are rubric-based, not citation-based) and
              when the asset has zero references. */}
          {!loading && data?.has_assessment && tab !== 'scoring' && refs.length > 0 && (
            <div className="md:w-80 md:shrink-0 max-h-[45vh] md:max-h-none overflow-hidden md:border-t-0 border-t border-slate-200">
              <ReferenceRail refs={refs} highlightN={highlightN} onDismissHighlight={() => setHighlightN(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExecBlock({ exec, refIds, onCite, maxRef }) {
  if (!exec || !Object.keys(exec).length) return <div className="text-slate-500 text-sm">No executive summary available.</div>;
  return (
    <div className="space-y-4">
      {exec.headline && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Headline</div>
          <p className="text-base text-slate-800 italic">
            &ldquo;<CitedText text={exec.headline} onCite={onCite} maxRef={maxRef} />&rdquo;
          </p>
        </div>
      )}
      {exec.rationale && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Rationale</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.rationale} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {exec.why_relevant && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Why Relevant</div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <CitedText text={exec.why_relevant} onCite={onCite} maxRef={maxRef} />
          </p>
        </div>
      )}
      {Array.isArray(exec.key_strengths) && exec.key_strengths.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1">Key Strengths</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {exec.key_strengths.map((s, i) => <li key={i}><CitedText text={s} onCite={onCite} maxRef={maxRef} /></li>)}
          </ul>
        </div>
      )}
      {Array.isArray(exec.key_risks || exec.risks) && (exec.key_risks || exec.risks).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1">Key Risks</div>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            {(exec.key_risks || exec.risks).map((s, i) => <li key={i}><CitedText text={s} onCite={onCite} maxRef={maxRef} /></li>)}
          </ul>
        </div>
      )}
      {exec.scientific_differentiation && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Scientific Differentiation</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.scientific_differentiation} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.commercial_opportunity && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Commercial Opportunity</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.commercial_opportunity} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.strategic_fit_for_viatris && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Strategic Fit for Viatris</div>
          <p className="text-sm text-slate-700 leading-relaxed"><CitedText text={exec.strategic_fit_for_viatris} onCite={onCite} maxRef={maxRef} /></p>
        </div>
      )}
      {exec.recommended_next_action && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold mb-1">Recommended Next Action</div>
          <p className="text-sm text-slate-700">
            {exec.recommended_next_action}
            {exec.next_action_rationale && (
              <span className="block text-xs text-slate-500 mt-1">
                <CitedText text={exec.next_action_rationale} onCite={onCite} maxRef={maxRef} />
              </span>
            )}
          </p>
        </div>
      )}
      <SectionRefStrip refIds={refIds} onCite={onCite} />
    </div>
  );
}

function ScoringBlock({ scoring, composite, weighted }) {
  if (!scoring || !Object.keys(scoring).length) return <div className="text-slate-500 text-sm">No scoring available.</div>;
  const dims = Object.entries(scoring).filter(([, v]) => v && typeof v === 'object' && 'score' in v);
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-4 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Weighted Avg</div>
          <div className="text-2xl font-bold text-indigo-700">
            {weighted != null ? weighted.toFixed(2) : (composite != null ? (composite / 9).toFixed(2) : '—')}<span className="text-sm font-normal text-slate-500"> / 5</span>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {dims.map(([key, val]) => {
          const score = Number(val.score) || 0;
          const pct = (score / 5) * 100;
          return (
            <div key={key} className="border border-slate-100 rounded p-3 bg-slate-50/40">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="text-xs font-semibold text-slate-800 capitalize">{key.replace(/_/g, ' ')}</div>
                <div className="text-sm font-bold text-indigo-700">{score}<span className="text-[10px] font-normal text-slate-500"> / 5</span></div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-2">
                <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
              </div>
              {val.rationale && <p className="text-[11px] text-slate-700 leading-relaxed">{val.rationale}</p>}
              {val.confidence && <div className="text-[10px] text-slate-500 mt-1">Confidence: <strong>{val.confidence}</strong></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// SectionBlock, renderValue, AlertsBlock, RefsBlock, CitedText,
// SectionRefStrip, and ReferenceRail were removed on Feb 2026
// and now live in /app/frontend/src/lib/deepDiveRender.js — a
// shared library so this page and ViatrisClientDashboard.js stay
// visually identical without drift.
